import React from "react";
import styled from "@emotion/styled";
import UserImage from "@components/item/UserImage";
import { useTranslation } from "react-i18next";
import Image from "@components/item/Image";
import { useTheme } from "@emotion/react";
import StarRating from "@components/item/StarRating";
import { useNavigate, useParams } from "react-router-dom";
import FollowButton from "@components/button/FollowButton";
import TabWithPanel from "@components/tab/TabWithPanel";
import { InfoPanel, ShopListPanel } from "@pages/myPage/_component";
import { useApiRequest } from "@utils/ApiRequest";
import { useQuery } from "@tanstack/react-query";
import useUserInfoStore from "@store/useUserInfoStore";
import {
  SellerInfoPanel,
  WishlistPanel,
} from "@pages/seller/_components/index";

const Container = styled.div(({ theme }) => ({
  position: "relative",
  display: "flex",
  flexDirection: "column",
  gap: "2rem",
  // padding: "2rem",
}));

const ProfileContainer = styled.div(({ theme }) => ({
  display: "flex",
  gap: "2.4rem",
  alignItems: "center",
  border: "none",
  padding: "2rem",
}));

const PaddingContainer = styled.div({
  display: "flex",
  width: "100%",
  padding: "0 2rem",
});

const InfoWrapper = styled.div({
  display: "flex",
  flexDirection: "column",
  gap: "1rem",
});

const NicknameBox = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "1rem",
  fontSize: "1.6rem",
  fontWeight: theme.fontWeight.medium,
  lineHeight: "2.4rem",

  ".sell_count": {
    color: "#969696",
    fontWeight: theme.fontWeight.regular,
    fontSize: "1.2rem",
    lineHeight: "2rem",
  },
}));

const SoldCntBox = styled.div(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: "0.5rem",
  fontSize: "1.2rem",
  lineHeight: "1.2rem",
  color: theme.color.dark_gray,
  fontWeight: theme.fontWeight.light,
}));

const FollowWrapper = styled.div({
  display: "flex",
  gap: "4.8rem",
});

const FollowBox = styled.div(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: "0.5rem",
  fontWeight: theme.fontWeight.regular,
  fontSize: "1.6rem",

  ".number": {
    fontWeight: theme.fontWeight.bold,
    fontSize: "1.6rem",
    lineHeight: "2rem",
  },
}));

const SellerInfo = ({}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const theme = useTheme();
  const params = useParams();
  const sellerId = Number(params.sellerId);
  const { getSellerInfo, getSellerFollowCount } = useApiRequest();
  const { userInfo } = useUserInfoStore();

  const { data: sellerInfo, isLoading: userLoading } = useQuery({
    queryKey: ["sellerInfo", sellerId],
    queryFn: () => getSellerInfo(sellerId),
  });
  const sellerNickname = sellerInfo?.nickname;

  const { data: sellerFollowCount, isLoading: sellerFollowLoading } = useQuery({
    queryKey: ["sellerFollowCount"],
    queryFn: () => getSellerFollowCount(sellerId),
  });

  const handleNavigatePage = (route: string) => {
    navigate(route);
  };

  const handleNavigate = () => {
    navigate(`/follow/${sellerId}`, {
      state: {
        userName: sellerNickname,
      },
    });
  };

  const item = [
    {
      label: `SHOP (${sellerInfo?.total_product_count})`,
      key: "1",
      children: <ShopListPanel sellerId={sellerId} />,
    },
    {
      label: "WISH LIST",
      key: "2",
      children: <WishlistPanel sellerId={sellerId} />,
    },
    {
      label: "INFO",
      key: "3",
      children: <SellerInfoPanel sellerInfo={sellerInfo} />,
    },
  ];

  return userLoading || sellerFollowLoading ? (
    <></>
  ) : (
    <Container>
      <ProfileContainer>
        <UserImage size="8rem" src={sellerInfo?.profile_image} />
        <InfoWrapper>
          <NicknameBox>{sellerInfo.nickname}</NicknameBox>

          <StarRating
            rate={sellerInfo.rate}
            readonly
            reviewCnt={sellerInfo.review_count}
            onClick={() =>
              handleNavigatePage(`/review/${sellerInfo.seller_id}`)
            }
          />
        </InfoWrapper>
      </ProfileContainer>

      <PaddingContainer>
        <SoldCntBox>
          <Image
            src={theme.image.tag_icon.default}
            width="1.6rem"
            height="1.6rem"
          />
          <span>
            {sellerInfo.total_sales_amount} {t("판매 개수")}
          </span>
        </SoldCntBox>
      </PaddingContainer>

      <PaddingContainer onClick={handleNavigate}>
        <FollowWrapper>
          <FollowBox>
            <span className="number">{sellerFollowCount?.follower_count}</span>
            <span>{t("팔로워")}</span>
          </FollowBox>

          <FollowBox>
            <span className="number">{sellerFollowCount?.following_count}</span>
            <span>{t("팔로잉")}</span>
          </FollowBox>
        </FollowWrapper>
      </PaddingContainer>

      {userInfo?.id !== sellerInfo?.seller_id && (
        <PaddingContainer>
          <FollowButton
            isFollow={sellerInfo?.is_followed !== 0}
            full
            id={sellerInfo?.seller_id}
          />
        </PaddingContainer>
      )}

      <TabWithPanel item={item} defaultValue={"1"} />
    </Container>
  );
};

export default SellerInfo;
